import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Products } from 'merchandise-lib/constants/Product';
import { NavMarker } from 'react-rhumb';
import FeatureTable from 'ui-addon-upgrades/featureTable/FeatureTable';
import { useAppSelector } from '../../../reducers';
import { isUngatedForSeatsSelector } from '../../../selectors/GatingSelectors';
import { sortSkuCategoryNames } from '../../../utils/sortingUtils';
import { CustomError } from '../../../v2/components/error-boundary/CustomErrors';
import { useErrorBoundary } from '../../../v2/components/error-boundary/useErrorBoundary';
import { withErrorBoundary } from '../../../v2/components/error-boundary/withErrorBoundary';
import { useBadges } from '../../../v2/hooks/badges/useBadges';
import { usePinnedFeaturesBySkuCategoryName } from '../../../v2/hooks/features/useFeatures';
import { getHighlightedProductsForSkuCategory } from '../../../v2/utils/highlightedProductUtils';
import { FeatureTableLabelContent } from '../shared/FeatureTableLabelContent';
import GenericBundleFeatureTableHeaderCell from './GenericBundleFeatureTableHeaderCell';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getApiNamesForSkuCategory = skuCategoryName => {
  const {
    freeHighlightedProduct,
    paidHighlightedProducts
  } = getHighlightedProductsForSkuCategory({
    skuCategoryName
  });
  const apiNames = [];
  if (freeHighlightedProduct) {
    apiNames.push(freeHighlightedProduct);
  }
  if (paidHighlightedProducts !== null && paidHighlightedProducts !== void 0 && paidHighlightedProducts[0]) {
    apiNames.push(paidHighlightedProducts[0]);
  }
  return apiNames;
};
const buildFeatureTableHeaderData = (apiNames, screen, trackingSource, badges) => {
  return apiNames.reduce((headers, apiName) => {
    var _badges$apiName;
    headers[apiName] = {
      Content: /*#__PURE__*/_jsx(GenericBundleFeatureTableHeaderCell, {
        apiName: apiName,
        screen: screen,
        trackingSource: trackingSource,
        badgeName: badges === null || badges === void 0 || (_badges$apiName = badges[apiName]) === null || _badges$apiName === void 0 ? void 0 : _badges$apiName.badgeName
      }, apiName)
    };
    return headers;
  }, {});
};
const buildFeatureTableBodyData = ({
  features,
  subscreen,
  skuCategoryNames
}) => {
  const sectionData = skuCategoryNames.reduce((sectionDataMap, skuCategoryName) => {
    const apiNames = getApiNamesForSkuCategory(skuCategoryName);
    sectionDataMap[skuCategoryName] = {
      features: features[skuCategoryName] || [],
      LabelContent: /*#__PURE__*/_jsx(FeatureTableLabelContent, {
        skuCategoryName: skuCategoryName
      }),
      apiNames
    };
    return sectionDataMap;
  }, {});
  if (Object.keys(sectionData).length > 0) {
    return {
      sharedData: {
        subscreen
      },
      sectionData
    };
  }
  return null;
};
const FreeFeatureTable = ({
  screen,
  trackingSource
}) => {
  const skuCategoryNames = ['MARKETING_PRODUCTS', 'SALES_PRODUCTS', 'SERVICE_HUB_PRODUCTS', 'CMS_HUB_PRODUCTS', 'OPERATIONS_HUB_PRODUCTS'];
  const {
    showBoundary
  } = useErrorBoundary();
  const isSeatsRedesignUngated = useAppSelector(isUngatedForSeatsSelector);
  const apiNamesBySku = skuCategoryNames.reduce((acc, skuCategoryName) => {
    acc[skuCategoryName] = getApiNamesForSkuCategory(skuCategoryName);
    return acc;
  }, {});
  const {
    featuresBySkuCategoryName,
    loading,
    error
  } = usePinnedFeaturesBySkuCategoryName(apiNamesBySku);
  const allApiNames = [Products.HUBSPOT_FREE, Products.CRM_SUITE_STARTER_BUNDLE];
  const {
    badges
  } = useBadges({
    productApiNames: allApiNames
  });
  const sortedSkuCategoryNames = sortSkuCategoryNames(skuCategoryNames);
  if (loading) return /*#__PURE__*/_jsx(LoadingSpinner, {
    grow: true
  });
  if (error) {
    showBoundary(new CustomError(error.message || 'Error fetching features', {
      error,
      skuCategoryNames,
      fingerprint: ['errorFetchingFeatures']
    }));
  }
  if (!featuresBySkuCategoryName || !Object.keys(featuresBySkuCategoryName).length) {
    return null;
  }
  const tableHeader = buildFeatureTableHeaderData(allApiNames, screen, trackingSource, badges);
  const tableBody = buildFeatureTableBodyData({
    features: featuresBySkuCategoryName,
    subscreen: screen,
    skuCategoryNames: sortedSkuCategoryNames
  });
  if (!tableBody) return null;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: "FREE_FEATURE_TABLE"
    }), /*#__PURE__*/_jsx("div", {
      "data-selenium": "free-feature-table",
      id: "feature-table",
      children: /*#__PURE__*/_jsx(FeatureTable, {
        headerData: tableHeader,
        bodyData: tableBody,
        canViewNewSeats: isSeatsRedesignUngated,
        isInbound25PricingPage: true
      })
    })]
  });
};
export default withErrorBoundary(FreeFeatureTable, {
  fallback: null,
  errorBoundaryName: 'FREE_FEATURE_TABLE'
});